import { useMemo } from 'react';
import Dotdotdot from 'react-dotdotdot';
import styled from 'styled-components';

import { BlurredBackground } from '@hivebrite/design-system/BlurredBackground';
import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import { Container } from '@hivebrite/design-system/Container';
import { Image } from '@hivebrite/design-system/Image';
import {
  primaryShadow,
  responsive,
  zIndices,
  useBreakpoint,
} from '@hivebrite/design-system/Theme';

import SidePreview from 'hb-react/frontoffice/components/SidePreview';
import TopicMemberRequestContainer from 'hb-react/shared/apps/Topics/containers/TopicMemberRequestContainer/index';
import Grid, { Col, Row } from 'hb-react/shared/components/DesignSystem/Grid';
import SearchButton from 'hb-react/shared/components/SearchButton';
import useDisplayBlurEffects from 'hb-react/shared/hooks/theme/useDisplayBlurEffects';
import useGroupNavigationTheme from 'hb-react/shared/hooks/theme/useGroupNavigationTheme';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import ThemeProvider from 'hb-react/style/ThemeProvider';
import { enableScroll } from 'helpers/scroll';

import MemberActionsButton from '../TopicHeader/components/MemberActionsButton';
import Members from '../TopicHeader/components/Members';
import TopicNavigationRow from '../TopicHeader/components/TopicNavigationRow';

const TopicHeader = ({
  currentNetwork,
  currentUser,
  socialLinks,
  topic,
  topicsActions,
  topicMenuRef,
  following,
  members,
  unfollowTopic,
  updateSettings,
}) => {
  const { flagUeSearchDeactivateGlobal } = useFeatureFlag();
  const groupNavigationTheme = useGroupNavigationTheme();
  const displayBlurEffects = useDisplayBlurEffects();
  const breakpoint = useBreakpoint();
  const shouldDisplayLogo =
    topic.logo_url &&
    !/^(\/images\/missing|\/logos\/default\/missing)/.test(topic.logo_url);

  // Transform data from model in plain object to display members list
  const displayMembers = useMemo(() => {
    if (currentUser && topic.followings) {
      return topic.followings.reduce((res, id) => {
        const user = members.getIn(['followings', id.toString()])?.get('user');

        if (user) {
          res.push(user.toJS());
        }

        return res;
      }, []);
    }

    return null;
  }, [currentUser, topic, members]);

  const memberActionsContent = following ? (
    <MemberActionsButton
      following={following}
      onUnfollow={unfollowTopic}
      onUpdateSettings={updateSettings}
      topicClosed={topic?.closed}
    />
  ) : (
    !topic?.closed && <TopicMemberRequestContainer />
  );

  const styledColOffSet = (numCols) => ({
    _: 0,
    m: 0,
    l: 0,
    xl: 1 / numCols,
  });

  const styledColWidth = (numCols) => ({
    _: 12 / numCols,
    m: 12 / numCols,
    l: 12 / numCols,
    xl: 10 / numCols,
  });

  const coverPictureUrl =
    breakpoint === 's'
      ? topic.cover_picture_small_url
      : topic.cover_picture_url;

  return (
    <ThemeProvider
      theme={groupNavigationTheme.themeName}
      fontBrightness={groupNavigationTheme.fontBrightness}
    >
      {breakpoint !== 's' && breakpoint !== 'm' && displayBlurEffects && (
        <BlurredBackground
          imageUrl={coverPictureUrl}
          height={{ l: '232px', xl: '292px' }}
        />
      )}
      <Container height="auto">
        <Grid width="100%" mx={breakpoint === 's' ? 0 : undefined}>
          <Row>
            <StyledCol
              offset={({ numCols }) => styledColOffSet(numCols)}
              width={({ numCols }) => styledColWidth(numCols)}
              growLeft="pageBorder"
              growRight="pageBorder"
            >
              <Content>
                <StyledContainer
                  $borderBottomRadius
                  $groupNavigationTheme={groupNavigationTheme}
                >
                  {breakpoint === 's' ? null : (
                    <Cover
                      style={{
                        backgroundImage: `url(${coverPictureUrl})`,
                      }}
                    />
                  )}
                  <Box flexDirection="column">
                    {breakpoint !== 's' &&
                      (shouldDisplayLogo || topic.can_edit) && (
                        <Box
                          mx={{ _: 4, m: 6 }}
                          pb={shouldDisplayLogo ? 3 : 0}
                          mb={shouldDisplayLogo ? -3 : 0}
                          position="relative"
                          zIndex={1}
                        >
                          <Ledge>
                            {shouldDisplayLogo ? (
                              <Box>
                                <Logo
                                  $groupNavigationTheme={groupNavigationTheme}
                                >
                                  <Image alt="" src={topic.logo_url} />
                                </Logo>
                              </Box>
                            ) : null}
                            {topic.can_edit && breakpoint !== 'm' ? (
                              <Box pb={shouldDisplayLogo ? 7 : 4}>
                                <SidePreview
                                  headerVisible={false}
                                  openByClickOn={
                                    <Button variant="inversed">
                                      {I18n.t('web.topics.manage')}
                                    </Button>
                                  }
                                  onOpen={() => enableScroll(false)}
                                  onClose={() => {
                                    topicsActions.fetchTopic(topic.id);
                                    enableScroll();
                                  }}
                                  url={Routes.backoffice_log_as_user_duallog_as_group_admin_path(
                                    {
                                      network_id: currentNetwork.id,
                                      topic_id: topic.id,
                                    },
                                  )}
                                />
                              </Box>
                            ) : null}
                          </Ledge>
                        </Box>
                      )}
                  </Box>
                </StyledContainer>
              </Content>
            </StyledCol>
          </Row>
        </Grid>
      </Container>
      <InfoAndNavigationWrapper
        breakpoint={breakpoint}
        $groupNavigationTheme={groupNavigationTheme}
      >
        <Container height="auto">
          <Grid width="100%" mx={breakpoint === 's' ? 0 : undefined} rowGap={0}>
            <Row>
              <StyledCol
                offset={({ numCols }) => styledColOffSet(numCols)}
                width={({ numCols }) => styledColWidth(numCols)}
                growLeft="pageBorder"
                growRight="pageBorder"
              >
                <Content>
                  <StyledContainer
                    mt={0}
                    breakpoint={breakpoint}
                    pt={shouldDisplayLogo ? 3 : 0}
                    $borderTopRadius
                    $groupNavigationTheme={groupNavigationTheme}
                  >
                    <Box flexDirection="column">
                      <TopicInfoRow breakpoint={breakpoint}>
                        <Box flexGrow={1} alignItems="center">
                          <GroupTitle>
                            <Dotdotdot clamp={2} useNativeClamp>
                              {topic.name}
                            </Dotdotdot>
                          </GroupTitle>
                        </Box>
                        {currentUser ? (
                          <>
                            {breakpoint !== 's' && (
                              <Box
                                pt={2}
                                pl={3}
                                alignItems="flex-start"
                                flexShrink={0}
                              >
                                <Members
                                  topicId={topic?.id}
                                  users={displayMembers}
                                  totalUsers={members.get('total')}
                                />
                              </Box>
                            )}
                            {!flagUeSearchDeactivateGlobal && (
                              <SearchButtonWrapper>
                                <SearchButton
                                  ariaLabel={s__(
                                    'Topics|Search %{topic} (opens a new page)',
                                  ).i__({
                                    topic: topic?.name,
                                  })}
                                  scopeType="topic"
                                  scopeObject={topic}
                                />
                              </SearchButtonWrapper>
                            )}
                            <ThemeProvider
                              theme={groupNavigationTheme.themeName}
                            >
                              <MemberActions>
                                {memberActionsContent}
                              </MemberActions>
                            </ThemeProvider>
                          </>
                        ) : null}
                      </TopicInfoRow>
                      <TopicNavigationRow
                        socialLinks={socialLinks}
                        topic={topic}
                        topicMenuRef={topicMenuRef}
                      />
                    </Box>
                  </StyledContainer>
                </Content>
              </StyledCol>
            </Row>
          </Grid>
        </Container>
      </InfoAndNavigationWrapper>
    </ThemeProvider>
  );
};

export default TopicHeader;

const StyledCol = styled(Col)`
  > div {
    margin-left: 0;
    margin-right: 0;
  }
`;

const Content = styled(Box).attrs(() => ({
  flexDirection: 'column',
}))``;

const StyledContainer = styled(Box).attrs(({ mt }) => ({
  flexDirection: 'column',
  justifyContent: 'start',
  position: 'relative',
  mt: mt ?? {
    _: 0,
    m: 8,
  },
}))`
  background-color: ${({ $groupNavigationTheme }) =>
    $groupNavigationTheme.backgroundColor};
  ${({ breakpoint }) => (breakpoint !== 'xl' ? primaryShadow : null)};
  ${responsive('m')`
      border-top-left-radius: ${({ theme, $borderTopRadius }) =>
        $borderTopRadius ?? `${theme.themeVariables.globalBorderRadius}px`};
      border-top-right-radius: ${({ theme, $borderTopRadius }) =>
        $borderTopRadius ?? `${theme.themeVariables.globalBorderRadius}px`};
    `}

  ${responsive('m')`
      border-bottom-left-radius: ${({ theme, $borderBottomRadius }) =>
        $borderBottomRadius ?? `${theme.themeVariables.globalBorderRadius}px`};
      border-bottom-right-radius: ${({ theme, $borderBottomRadius }) =>
        $borderBottomRadius ?? `${theme.themeVariables.globalBorderRadius}px`};
    `}
`;

const Cover = styled(Box).attrs({
  height: {
    _: '172px',
    m: '175px',
    l: '220px',
    xl: '260px',
  },
})`
  background-size: cover;
  background-position: center;
  ${responsive('m')`
      border-top-left-radius: ${({ theme }) =>
        `${theme.themeVariables.globalBorderRadius}px`};
      border-top-right-radius: ${({ theme }) =>
        `${theme.themeVariables.globalBorderRadius}px`};
    `}
`;

const Ledge = styled(Box)`
  position: absolute;
  bottom: 0;
  align-items: flex-end;
`;

const Logo = styled(Box).attrs({
  p: 1,
  mr: 5,
  mb: -1,
  width: '90px',
  height: '90px',
})`
  background-color: ${({ $groupNavigationTheme }) =>
    $groupNavigationTheme.backgroundColor};
  ${primaryShadow}
`;

const TopicInfoRow = styled(Box)
  .withConfig({
    shouldForwardProp: (prop) => !['breakpoint'].includes(prop),
  })
  .attrs({
    pb: 4,
    mx: { _: 4, m: 6, xl: 0 },
    flexDirection: 'row',
    position: 'relative',
    alignItems: { m: 'center' },
    flexWrap: { _: 'wrap', m: 'nowrap' },
  })`
  border-bottom: 1px solid ${({ theme }) => theme.colors.horizon};
  border-bottom-width: ${(props) => (props.breakpoint === 's' ? 0 : 1)};
  z-index: ${zIndices.positioned + 2};
`;

const GroupTitle = styled(Box).attrs({
  as: 'h1',
  font: 'h2-semibold',
  pt: 4,
  pr: { _: 0, m: 8 },
  width: '100%',
})``;

const MemberActions = styled(Box).attrs(() => ({
  pt: { _: 4, m: 3, l: 3, xl: 3 },
  pl: { _: 0, m: 3 },
  alignItems: 'flex-start',
  flexShrink: 0,
}))``;

const InfoAndNavigationWrapper = styled(Box)
  .withConfig({
    shouldForwardProp: (prop) => !['breakpoint'].includes(prop),
  })
  .attrs({})`
  background-color: ${({ breakpoint, $groupNavigationTheme }) =>
    breakpoint === 'xl' ? $groupNavigationTheme.backgroundColor : null};
  ${({ breakpoint }) => (breakpoint === 'xl' ? primaryShadow : null)};
`;

const SearchButtonWrapper = styled(Box).attrs(() => ({
  ml: 4,
  pt: { _: 4, m: 3 },
  order: { xs: 3, s: 3, m: 'unset' },
}))``;
